import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { otpSend, otpVerify, createLead, formatApiError } from "@/lib/api";

export default function LeadWizard({ source = "Hero Wizard", luxe = false }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", whatsapp: true,
    email: "", property_type: "", property_name: "", location: "", otp: "",
  });
  const up = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // Style variants
  const card = luxe
    ? "bg-warm-ivory/95 backdrop-blur-sm p-6 md:p-7 luxe-shadow w-full"
    : "bg-warm-ivory p-7 md:p-8 soft-shadow w-full";
  const heading = luxe
    ? "font-display text-2xl md:text-[1.75rem] leading-tight text-espresso"
    : "font-display text-3xl md:text-4xl text-espresso leading-none";
  const subHeading = "text-espresso/55 text-[0.78rem] mt-1.5 fine-uppercase";
  const label = "text-[0.62rem] fine-uppercase text-champagne block mb-1.5";
  const field =
    "w-full bg-transparent border-b border-espresso/25 px-0 py-2 text-[0.95rem] text-espresso font-light focus:border-champagne focus:outline-none transition-colors placeholder:text-espresso/30";
  const primaryBtn =
    "w-full inline-flex items-center justify-center gap-2.5 bg-espresso text-warm-ivory fine-uppercase text-[0.7rem] px-6 py-3.5 hover:bg-walnut transition-colors disabled:opacity-60";
  const secondaryBtn =
    "inline-flex items-center justify-center gap-1.5 border border-espresso/30 text-espresso fine-uppercase text-[0.7rem] px-5 py-3.5 hover:bg-espresso hover:text-warm-ivory transition-colors";

  const next = async () => {
    if (!form.name.trim() || form.phone.trim().length < 6) {
      toast.error("Please enter your name and a valid phone number.");
      return;
    }
    setLoading(true);
    try {
      const res = await otpSend(form.phone.trim());
      setStep(2);
      toast.success("OTP sent to your phone.");
      if (res.dev_otp) toast.info(`Demo OTP: ${res.dev_otp}`, { duration: 8000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const res = await otpSend(form.phone.trim());
      toast.success("OTP resent.");
      if (res.dev_otp) toast.info(`Demo OTP: ${res.dev_otp}`, { duration: 8000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submit = async () => {
    if (form.otp.trim().length < 4) {
      toast.error("Enter the OTP sent to your phone.");
      return;
    }
    setLoading(true);
    try {
      await otpVerify(form.phone.trim(), form.otp.trim());
      await createLead({
        name: form.name,
        phone: form.phone,
        email: form.email,
        location: form.location,
        property_type: form.property_type,
        property_name: form.property_name,
        message: form.whatsapp ? "Opted in for WhatsApp updates." : "",
        lead_source: source,
        verified: true,
      });
      setDone(true);
      toast.success("Verified! Our studio will connect with you within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div data-testid="wizard-success" className={`${card} text-center`}>
        <ShieldCheck className="mx-auto text-champagne" size={38} strokeWidth={1.3} />
        <p className="font-display text-3xl text-espresso mt-4">Thank you.</p>
        <p className="text-espresso/65 mt-3 text-[0.95rem] font-light leading-relaxed">Your phone is verified. A studio principal will connect with you within 24 hours.</p>
      </div>
    );

  return (
    <div data-testid="lead-wizard" className={card}>
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="champagne-rule" style={{ width: "1.25rem" }} />
            <span className="fine-uppercase text-[0.62rem] text-champagne">Private Consultation</span>
          </div>
          <h3 className={heading}>Reserve your studio time</h3>
          <p className={subHeading}>Step {step} of 2</p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div key="s1" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.3 }} className="space-y-5" data-testid="wizard-step-1">
            <div>
              <label className={label}>Your name</label>
              <input data-testid="wizard-name" className={field} value={form.name} onChange={up("name")} placeholder="Full name" />
            </div>
            <div>
              <label className={label}>Phone number</label>
              <input data-testid="wizard-phone" className={field} value={form.phone} onChange={up("phone")} placeholder="98300 00000" inputMode="tel" />
            </div>
            <label className="flex items-center gap-3 text-[0.85rem] text-espresso/70 font-light cursor-pointer pt-1">
              <input data-testid="wizard-whatsapp" type="checkbox" checked={form.whatsapp} onChange={up("whatsapp")} className="w-3.5 h-3.5 accent-[#29231F]" />
              Send me studio updates on WhatsApp
            </label>
            <button data-testid="wizard-next" onClick={next} disabled={loading} className={primaryBtn}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              Continue
              <ArrowRight size={14} strokeWidth={1.6} />
            </button>
          </motion.div>
        ) : (
          <motion.div key="s2" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.3 }} className="space-y-4" data-testid="wizard-step-2">
            <div>
              <label className={label}>Email</label>
              <input data-testid="wizard-email" type="email" className={field} value={form.email} onChange={up("email")} placeholder="you@email.com" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Property type</label>
                <input data-testid="wizard-property" className={field} value={form.property_type} onChange={up("property_type")} placeholder="3 BHK" />
              </div>
              <div>
                <label className={label}>Property name</label>
                <input data-testid="wizard-property-name" className={field} value={form.property_name} onChange={up("property_name")} placeholder="Building / society" />
              </div>
            </div>
            <div>
              <label className={label}>Location</label>
              <input data-testid="wizard-location" className={field} value={form.location} onChange={up("location")} placeholder="Area, city" />
            </div>
            <div className="pt-1">
              <label className={label}>Enter OTP</label>
              <input data-testid="wizard-otp" className={`${field} tracking-[0.5em] text-center`} value={form.otp} onChange={up("otp")} placeholder="••••••" inputMode="numeric" maxLength={6} />
              <button type="button" data-testid="wizard-resend" onClick={resend} className="text-[0.7rem] text-champagne link-underline mt-2 fine-uppercase">Resend OTP</button>
            </div>
            <div className="flex gap-3 pt-2">
              <button data-testid="wizard-back" onClick={() => setStep(1)} className={secondaryBtn}>
                <ArrowLeft size={14} strokeWidth={1.6} /> Back
              </button>
              <button data-testid="wizard-submit" onClick={submit} disabled={loading} className={`${primaryBtn} flex-1`}>
                {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                Verify &amp; Submit
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
